'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { type ResponsiveImageAttributes, getResponsiveData } from './utils/getResponsiveData';
import type { BreakpointValue, ImageProps } from './types';
import { useIntersectionObserver } from 'hooks';
import {
  Breakpoints,
  ImageFetchPriority,
  ImageLoading,
  ImageTrim,
  DESKTOP_IMAGE_WIDTH,
  DESKTOP_BREAKPOINT_DEFAULT,
  MOBILE_BREAKPOINT_DEFAULT,
  ROOT_MARGIN_INTERSECTION,
  RESPONSIVE_DATA_DEFAULT
} from './constants';

/**
 * Responsive Image component with lazy loading and intersection observer support. It adjusts image attributes based on
 * device type and specified breakpoints automatically if not explicity provided.
 *
 * @param props - Responsive image properties
 * @returns A JSX element rendering an image
 */
const Image = (props: ImageProps) => {
  const {
    image,
    className,
    testId,
    loading = ImageLoading.LAZY,
    trim = ImageTrim.AUTO,
    device,
    breakpoint = device === 'mobile' ? MOBILE_BREAKPOINT_DEFAULT : DESKTOP_BREAKPOINT_DEFAULT,
    rootMarginIntersection = ROOT_MARGIN_INTERSECTION,
    fetchPriority = ImageFetchPriority.Low,
    onClick
  } = props;
  const minWidth: BreakpointValue = Breakpoints[breakpoint];
  const isEager = loading === ImageLoading.EAGER;

  const imageAttributes: ResponsiveImageAttributes | null = useMemo(() => {
    if (!image) return RESPONSIVE_DATA_DEFAULT;

    return getResponsiveData(image, trim, minWidth, DESKTOP_IMAGE_WIDTH);
  }, [image, trim, minWidth]);

  const [hydrated, setHydrated] = useState(false);
  // Eager skips low res iamges to avoid a double fetch on above-fold images.
  const [imageSrc, setImageSrc] = useState(isEager ? imageAttributes.src : imageAttributes.lowSrc);
  const [imageSrcSet, setImageSrcSet] = useState(isEager ? imageAttributes.srcSet || '' : '');
  const { ref, isIntersecting } = useIntersectionObserver<HTMLImageElement>({
    rootMargin: rootMarginIntersection
  });
  const safeSizes = hydrated ? imageAttributes?.sizes : '100vw';

  useEffect(() => {
    if (isIntersecting) {
      setImageSrc(imageAttributes.src);

      setImageSrcSet(imageAttributes.srcSet || '');
    }
  }, [isIntersecting, imageAttributes.src, imageAttributes.srcSet]);

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <img
      alt={image?.alt}
      className={className}
      data-testid={testId}
      fetchPriority={fetchPriority}
      height={image?.imageTagHeight}
      loading={loading}
      onClick={onClick}
      sizes={safeSizes}
      src={imageSrc}
      ref={ref}
      srcSet={imageSrcSet}
      title={image?.title}
      width={image?.imageTagWidth}
    />
  );
};

export { Image };
